import { apiService } from "./api.service";
import { STORAGE_KEYS } from "../config/constants";
import {
  LoginCredentials,
  LoginResponse,
  User,
  ServicesResponse,
  Service,
} from "../types";

class AuthService {
  async login(
    credentials: LoginCredentials
  ): Promise<{
    token?: string;
    user?: User;
    requiresTwoFactor?: boolean;
    requiresTwoFactorSetup?: boolean;
    userId?: number;
  }> {
    const response = await apiService.post<LoginResponse>(
      "/auth/login",
      credentials
    );

    if (response.success) {
      if (response.requiresTwoFactorSetup && response.data?.userId) {
        return {
          requiresTwoFactorSetup: true,
          userId: response.data.userId,
        };
      }

      // Nếu yêu cầu 2FA
      if (response.requiresTwoFactor && response.data?.userId) {
        return {
          requiresTwoFactor: true,
          userId: response.data.userId,
        };
      }

      // Đăng nhập thành công
      if (response.data?.token && response.data?.user) {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.token);
        localStorage.setItem(
          STORAGE_KEYS.USER_DATA,
          JSON.stringify(response.data.user)
        );
        return {
          token: response.data.token,
          user: response.data.user,
        };
      }
    }

    throw new Error(response.message || "Đăng nhập thất bại");
  }

  async logout(): Promise<void> {
    try {
      await apiService.post("/auth/logout");
    } catch {
      // Không chặn luồng logout ở client nếu backend tạm thời lỗi.
    } finally {
      this.clearStorage();
    }
  }

  clearStorage(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  getStoredUser(): User | null {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (userData) {
        return JSON.parse(userData);
      }
      return null;
    } catch {
      return null;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  async getAvailableServices(): Promise<Service[]> {
    const response = await apiService.get<ServicesResponse>("/auth/services");
    if (response.success) {
      return response.data;
    }
    return [];
  }

  async forgotPassword(email: string): Promise<void> {
    await apiService.post("/auth/forgot-password", { email });
  }

  async verifyOTP(email: string, otp: string): Promise<void> {
    await apiService.post("/auth/verify-otp", { email, otp });
  }

  async resetPassword(email: string, newPassword: string): Promise<void> {
    await apiService.post("/auth/reset-password", { email, newPassword });
  }
}

export const authService = new AuthService();
